import React from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useAdmin } from '../components/Admin/AdminContext';
import AdminLogin from '../components/Admin/AdminLogin';
import AdminDashboard from './Admin/AdminDashboard';
import AdminUsers from './Admin/AdminUsers';
import AdminSpots from './Admin/AdminSpots';
import AdminStatistics from './Admin/AdminStatistics';
import AdminPhotos from './Admin/AdminPhotos';
import AdminComments from './Admin/AdminComments';
import AdminModeration from './Admin/AdminModeration';
import AdminDatabase from './Admin/AdminDatabase';
import AdminTags from './Admin/AdminTags';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, Users, MapPin, Settings, LogOut, 
  Shield, Activity, BarChart3, Menu, X, Image, MessageCircle,
  Clock, Database, Tag
} from 'lucide-react';

export default function AdminPanel() {
  const { isAdminAuthenticated, adminUser, adminLogout, loading } = useAdmin();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-lg text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!isAdminAuthenticated) {
    return <AdminLogin />;
  }

  const navigation = [
    { name: 'Tableau de bord', href: '/admin', icon: LayoutDashboard, current: location.pathname === '/admin' },
    { name: 'Modération', href: '/admin/moderation', icon: Clock, current: location.pathname === '/admin/moderation', highlight: true },
    { name: 'Utilisateurs', href: '/admin/users', icon: Users, current: location.pathname === '/admin/users' },
    { name: 'Spots', href: '/admin/spots', icon: MapPin, current: location.pathname === '/admin/spots' },
    { name: 'Photos', href: '/admin/photos', icon: Image, current: location.pathname === '/admin/photos' },
    { name: 'Commentaires', href: '/admin/comments', icon: MessageCircle, current: location.pathname === '/admin/comments' },
    { name: 'Tags', href: '/admin/tags', icon: Tag, current: location.pathname === '/admin/tags' },
    { name: 'Statistiques', href: '/admin/stats', icon: BarChart3, current: location.pathname === '/admin/stats' },
    { name: 'Base de données', href: '/admin/database', icon: Database, current: location.pathname === '/admin/database' },
    { name: 'Paramètres', href: '/admin/settings', icon: Settings, current: location.pathname === '/admin/settings' },
  ];

  const Sidebar = () => (
    <div className="flex flex-col w-64 bg-white border-r border-gray-200 h-screen fixed left-0 top-0">
      {/* Header */}
      <div className="flex items-center justify-center h-16 px-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Shield className="h-8 w-8 text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
        </div>
      </div>

      {/* User Info */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-white">
              {adminUser?.username?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900">{adminUser?.username}</p>
            <p className="text-xs text-gray-500">{adminUser?.role}</p>
          </div>
        </div>
      </div>

      {/* Navigation - scrollable */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`${
                item.current
                  ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors`}
              onClick={() => setSidebarOpen(false)}
            >
              <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="px-2 py-4 border-t border-gray-200">
        <Button
          onClick={adminLogout}
          variant="ghost"
          className="w-full justify-start text-gray-700 hover:bg-gray-50 hover:text-gray-900"
        >
          <LogOut className="mr-3 h-5 w-5" />
          Se déconnecter
        </Button>
      </div>
    </div>
  );

  const MobileHeader = () => (
    <div className="lg:hidden">
      <div className="flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200">
        <div className="flex items-center">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-700 hover:bg-gray-100 p-2 rounded-md"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div className="ml-4 flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            <h1 className="text-lg font-bold text-gray-900">Admin</h1>
          </div>
        </div>
        <Button
          onClick={adminLogout}
          variant="ghost"
          size="sm"
          className="text-gray-700 hover:bg-gray-100 hover:text-gray-900"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <MobileHeader />

      {/* Desktop Sidebar - Fixed */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main Content - with left margin for fixed sidebar */}
      <main className="lg:pl-64 min-h-screen">
        <div className="p-4 lg:p-6">
        <Routes>
          <Route path="/" element={<AdminDashboard />} />
          <Route path="/moderation" element={<AdminModeration />} />
          <Route path="/users" element={<AdminUsers />} />
          <Route path="/spots" element={<AdminSpots />} />
          <Route path="/photos" element={<AdminPhotos />} />
          <Route path="/comments" element={<AdminComments />} />
          <Route path="/stats" element={<AdminStatistics />} />
          <Route path="/database" element={<AdminDatabase />} />
          <Route path="/tags" element={<AdminTags />} />
          <Route path="/settings" element={
            <div className="text-center py-12">
              <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-lg font-medium text-gray-900 mb-2">Paramètres Système</h2>
              <p className="text-gray-600">Cette fonctionnalité sera disponible prochainement</p>
            </div>
          } />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
        </div>
      </main>
    </div>
  );
}